import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import type { ChangelogUserFacingAnnouncements } from "@/features/changelog/api/changelog.api";
import { SettingRow, SettingsCard } from "../shared/SettingsCard";
import { USER_FACING_GATES } from "./changelog-settings-limits";
import type { ChangelogSettingsCardProps } from "./types";

/**
 * The in-app panel every user of the product gets. Kept out of the automation
 * presets on purpose, so turning automation up never floods users with small
 * details.
 */
export function UserFacingCard({ draft, set, isReadOnly }: ChangelogSettingsCardProps) {
  const { t } = useTranslation();

  return (
    <SettingsCard
      icon="eye"
      title={t("admin.changelog.settings.userFacing.title", "What users see")}
      description={t(
        "admin.changelog.settings.userFacing.description",
        "The in-app “What's new” panel every user of the product gets. Kept apart from the automation level so turning automation up never floods users with small details.",
      )}
    >
      <SettingRow
        label={t("admin.changelog.settings.userFacingGate", "Announce inside the app")}
        description={t(
          `admin.changelog.settings.userFacingGateHelp.${draft.userFacingAnnouncements}`,
          "",
        )}
        control={
          <Select
            value={draft.userFacingAnnouncements}
            disabled={isReadOnly}
            onValueChange={(value) =>
              set("userFacingAnnouncements", value as ChangelogUserFacingAnnouncements)
            }
          >
            <SelectTrigger className="h-8 w-56 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USER_FACING_GATES.map((gate) => (
                <SelectItem key={gate} value={gate} className="text-xs">
                  {t(`admin.changelog.settings.userFacingOption.${gate}`, gate)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </SettingsCard>
  );
}
